/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import * as uuid from '@base/node_modules/uuid';
import { Apollo } from 'apollo-angular';
import { CREATE_ORGANISATION_USER, DELETE_ORGANISATION_USER, GET_ORGANISATION_USERS_BY_ORG_ID, UPDATE_ORGANISATION_USER } from './organisation-user-queries';

@Injectable({
  providedIn: 'root',
})
export class OrganisationUserService {
  public organisationUser: OrganisationUsersModel;
  public organisationUserList: OrganisationUsersModel[];

  constructor(
    private apollo: Apollo
  ) {
  }

  public getOrganisationUsersByOrganisationId(organisationId: string): Observable<OrganisationUsersModel[]> {
    if (!!this.organisationUserList && this.organisationUserList.length > 0) {
      return new Observable(subscriber => {
        subscriber.next(this.organisationUserList);
        subscriber.complete();
      });
    } else {
      return new Observable(subscriber => {
        this.apollo.query({
          query: GET_ORGANISATION_USERS_BY_ORG_ID,
          variables: {
            condition: {
              organisationId: organisationId
            }
          }
        }).subscribe((result: any) => {
          this.organisationUserList = result.data.organisationUsers.map(orgUser => {
            return Object.assign(new OrganisationUsersModel, orgUser) });
          subscriber.next(this.organisationUserList);
          subscriber.complete();
        });
      });
    }
  }

  getOrganisationUserByUserId(userId: string): Observable<OrganisationUsersModel> {
    if (!!this.organisationUserList && this.organisationUserList.length > 0) {
      return new Observable(subscriber => {
        subscriber.next(this.organisationUserList.find(user => { return user.userId === userId}));
      })
    } else {
      return new Observable(subscriber => {
        this.getOrganisationUsersByOrganisationId(environment.product_owner_uuid)
          .subscribe(users => {
            subscriber.next(users.find(user => { return user.userId === userId}));
            subscriber.complete();
          });
      });
    }
  }

  updateOrganisationUser(user: OrganisationUsersModel): Observable<String> {
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: UPDATE_ORGANISATION_USER,
        variables: {
          input: {
            patch: user,
            userId: user.userId
          }
        }
      }).subscribe(res => {
        subscriber.next();
        subscriber.complete();
      })
    });
  }

  updateOrganisationUserSpecificVariable(userId: string, update: any ): Observable<OrganisationUsersModel> {
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: UPDATE_ORGANISATION_USER,
        variables: {
          input: {
            patch: update,
            userId: userId
          }
        }
      }).subscribe((res: any) => {
        this.organisationUser = res.data.updateOrganisationUser
        console.log(this.organisationUser);
        subscriber.next(this.organisationUser);
        subscriber.complete();
      })
    });
  }

  updateOrganisationUserSessionLock(userId: string, lock: boolean): Observable<String> {
    return new Observable(subscriber => {
      this.getOrganisationUserByUserId(userId)
        .subscribe(user => {
          user.sessionLocked = lock;
          this.updateOrganisationUser(user).subscribe(res => {
            subscriber.next(res);
            subscriber.complete();
          });
        });
    });
  }

  setNewOrgUserSession(userId: string, id: string): Observable<String> {
    if (!id) {
      id = uuid.v4();
    }
    return new Observable(subscriber => {
      this.getOrganisationUserByUserId(userId)
        .subscribe(user => {
          user.sessionId = id;
          user.sessionTimestamp = new Date();
          user.sessionLocked = false;
          this.updateOrganisationUser(user)
            .subscribe(res => {
              subscriber.next(res);
              subscriber.complete();
            })
        });
    });
  }

  createUser(userId: string, user?: OrganisationUsersModel): Observable<OrganisationUsersModel> {
    return new Observable<OrganisationUsersModel>(subscriber => {
      // first check if user exists
      this.getOrganisationUserByUserId(user.userId)
        .subscribe((res: OrganisationUsersModel) => {
          if (!res) {
            // if no user returned add them
            let organisationUser = new OrganisationUsersModel();
            if (!user) {
              const orgId = environment.product_owner_uuid;
              const userStatus = 'Approved';
              organisationUser.organisationId = orgId;
              organisationUser.updatedAt = null;
              organisationUser.userId = userId;
              organisationUser.organisationUserStatus = userStatus;
              organisationUser.updatedAt = null;
              organisationUser.roleId = null;
              organisationUser.sessionId = null;
              organisationUser.sessionTimestamp = null;
            } else {
              organisationUser = user;
            }
            this.apollo.mutate({
              mutation: CREATE_ORGANISATION_USER,
              variables: {
                input: {
                  organisationUser: user
                }
              }
            }).subscribe(() => {
              subscriber.next();
              subscriber.complete();
            })
          } else {
            subscriber.next(res);
            subscriber.complete();
          }
        });
    });
  }

  deleteOrganisationUser(userId: string): Observable<any> {
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: DELETE_ORGANISATION_USER,
        variables: {
          input: {
            userId: userId
          }
        }
      }).subscribe(res => {
        subscriber.next(res);
        subscriber.complete();
      })
    });
  }

}
